import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, ShieldCheck, Star, CheckCircle, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLOR = {
  platinum: '#E5E4E2',
  gold: '#F59E0B',
  silver: '#94A3B8',
  bronze: '#CD7F32',
  unverified: '#64748B',
};

/**
 * Supplier reputation banner — shown on /agents and /for-agents.
 * Fetches /api/agent/supplier-reputation once on mount.
 */
export const SupplierTrustBadge = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/agent/supplier-reputation`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const copyFeedUrl = () => {
    const url = `${window.location.origin}/api/agent/supplier-reputation`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
    }
    setCopied(true);
    toast.success('Reputation feed URL copied — paste into curl or share with an agent');
    setTimeout(() => setCopied(false), 2000);
  };

  if (error || !data) return null;

  const color = TIER_COLOR[data.tier] || '#64748B';

  if (compact) {
    return (
      <a href="/api/agent/supplier-reputation" target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-['JetBrains_Mono'] hover:bg-white/5 transition"
         style={{ borderColor: color, color }}
         data-testid="supplier-trust-badge-compact">
        <ShieldCheck size={14} weight="fill" />
        <span className="capitalize">{data.tier}</span>
        <span className="text-white/60">· {data.trust_score}/100</span>
      </a>
    );
  }

  return (
    <div className="bg-[#11141D] border rounded-lg p-5 flex flex-wrap items-center gap-6"
         style={{ borderColor: `${color}55` }}
         data-testid="supplier-trust-banner">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full flex items-center justify-center"
             style={{ background: `${color}22`, border: `1.5px solid ${color}` }}>
          <ShieldCheck size={28} style={{ color }} weight="fill" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xl font-['JetBrains_Mono'] font-bold text-white">Escrow-Protected</p>
            <span className="px-2 py-0.5 rounded text-xs font-['JetBrains_Mono'] font-bold capitalize"
                  style={{ background: `${color}22`, color }}>
              {data.tier} · {data.trust_score}/100
            </span>
          </div>
          <p className="text-xs text-[#94A3B8] mt-1">
            Funds held until tracking confirmed · {data.operator.years_in_business}+ yrs · {data.operator.certifications.join(' · ')}
          </p>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 min-w-0">
        <Stat
          label="Orders fulfilled"
          value={data.stats.shipped_or_completed}
          testid="stat-fulfilled"
        />
        <Stat
          label="Disputes"
          value={data.stats.disputed}
          hint={data.stats.total_orders > 0 ? `${Math.round(data.performance.zero_dispute_rate * 100)}% clean` : 'no data'}
          testid="stat-disputes"
        />
        <Stat
          label="Avg ship time"
          value={data.stats.total_orders > 0 ? `${data.performance.avg_ship_days.toFixed(1)}d` : '—'}
          hint={`${Math.round(data.performance.on_time_ship_rate * 100)}% on-time`}
          testid="stat-ship"
        />
        <Stat
          label="Unique buyers"
          value={data.stats.unique_buyer_wallets}
          testid="stat-buyers"
        />
      </div>
      <button onClick={copyFeedUrl}
         className="hidden md:inline-flex items-center gap-1 text-xs font-['JetBrains_Mono'] text-[#007AFF] hover:text-white transition"
         data-testid="supplier-trust-copy-feed"
         aria-label="Copy reputation feed URL">
        {copied ? <><CheckCircle size={12} weight="fill" className="text-[#10B981]" /> Copied</> : <><Copy size={12} /> Copy feed URL</>}
      </button>
    </div>
  );
};

const Stat = ({ label, value, hint, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono']">{label}</p>
    <p className="text-xl font-['JetBrains_Mono'] font-bold text-white">{value}</p>
    {hint && <p className="text-[10px] text-[#94A3B8]">{hint}</p>}
  </div>
);

/**
 * Wallet (buyer) reputation badge — shown in the connected-wallet panel.
 */
export const WalletReputationBadge = ({ address }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!address) return;
    setData(null);
    setError(false);
    axios.get(`${API_URL}/api/agent/wallet-reputation/${address}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }, [address]);

  if (!address || error) return null;
  if (!data) return (
    <div className="text-[11px] text-[#64748B] font-['JetBrains_Mono']">Loading reputation…</div>
  );

  const color = data.tier_badge?.color || '#94A3B8';
  const TierIcon = data.tier === 'elite' ? Star : data.tier === 'trusted' ? CheckCircle : Shield;

  return (
    <div className="flex flex-col items-center gap-1" data-testid="wallet-rep-badge">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-['JetBrains_Mono']"
           style={{ borderColor: color, color, background: `${color}11` }}>
        <TierIcon size={12} weight="fill" />
        <span>{data.tier_badge.label}</span>
        {data.stats.shipped_or_completed > 0 && (
          <span className="text-white/60">· {data.stats.shipped_or_completed}✓</span>
        )}
      </div>
      {data.stats.total_volume_usd > 0 && (
        <p className="text-[10px] text-[#64748B] font-['JetBrains_Mono']">
          ${data.stats.total_volume_usd.toLocaleString()} lifetime
        </p>
      )}
    </div>
  );
};

export default SupplierTrustBadge;
